import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminCourse, useUpdateCourseRoles } from "@/hooks/use-admin-courses";
import { JOB_ROLES } from "@/lib/roles";

interface SetCourseRolesDialogProps {
  course: AdminCourse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SetCourseRolesDialog({ course, open, onOpenChange }: SetCourseRolesDialogProps) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(course.targetRoles);
  const { mutate: updateRoles, isPending } = useUpdateCourseRoles();

  function toggleRole(value: string) {
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }

  function handleSave() {
    updateRoles(
      { courseId: course.id, targetRoles: selectedRoles },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Target Roles</DialogTitle>
          <DialogDescription>
            Select which job roles can see <strong>{course.title}</strong>. Leave all unchecked to
            show the course to everyone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {JOB_ROLES.map((role) => (
            <label
              key={role.value}
              htmlFor={`role-${role.value}`}
              className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 transition-colors hover:bg-muted/50"
            >
              <input
                id={`role-${role.value}`}
                type="checkbox"
                checked={selectedRoles.includes(role.value)}
                onChange={() => toggleRole(role.value)}
                className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
              />
              <span className="text-sm font-medium text-foreground">{role.label}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
